import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Calendar } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';

interface DueDateModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentDate: string;
  onSave: (date: string) => Promise<void>;
}

export function DueDateModal({
  isOpen,
  onClose,
  currentDate,
  onSave,
}: DueDateModalProps) {
  const { t } = useTranslation(['profile', 'common']);
  const [newDate, setNewDate] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Initialize date when modal opens
  useEffect(() => {
    if (isOpen) {
      setNewDate(currentDate.split('T')[0]);
    }
  }, [isOpen, currentDate]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!newDate) return;
    setIsSaving(true);
    try {
      await onSave(newDate);
      onClose();
    } catch (err) {
      console.error('Error saving due date:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const modalContent = (
    <>
      <div className="bottom-sheet-backdrop" onClick={onClose} />
      <div className="bottom-sheet-container">
        <div className="bottom-sheet-modal">
          {/* Header */}
          <div className="bottom-sheet-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-pp-orange/20 rounded-full flex items-center justify-center">
                <Calendar className="w-5 h-5 text-pp-orange" />
              </div>
              <h2>{t('profile:modals.dueDate.title')}</h2>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="bottom-sheet-content space-y-4">
            <p className="text-sm text-white/70">
              {t('profile:modals.dueDate.description')}
            </p>

            {/* Date Input */}
            <div className="p-3 bg-white/10 rounded-[15px]">
              <input
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-[10px] text-white focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
              />
            </div>

            {/* Hint */}
            <p className="text-xs text-white/50">
              {t('profile:modals.dueDate.hint')}
            </p>
          </div>

          {/* Footer */}
          <div className="bottom-sheet-footer">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30 disabled:opacity-50"
            >
              {t('common:cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || !newDate}
              className="flex-1 py-2.5 rounded-xl bg-pp-orange text-white font-medium hover:bg-pp-orange/90 disabled:opacity-50 flex items-center justify-center"
            >
              {isSaving ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                t('common:save')
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
}
